import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as React from "react";
import { Button } from "@radix-ui/themes";
import { config } from "@/config/env";
import { authService } from "@/services/breeze/AuthService";
import { userService } from "@/services/breeze/UserService";
import { tenantService } from "@/services/breeze/TenantService";
import { getAuthClient, TENANT_ID_KEY } from "@/lib/auth";
import toast from "react-hot-toast";

const authClient = getAuthClient();

// Track tokens currently being exchanged to prevent duplicate calls in React StrictMode
const exchangingTokens = new Set<string>();

const Transfer = () => {
  const navigate = useNavigate();
  const hasTransferredRef = useRef(false);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const transferToken = urlParams.get("transfer_token");
    const targetOrgId = urlParams.get("target_org_id");

    // Check for invalid transfer request up front
    if (!transferToken || !targetOrgId) {
      setInvalid(true);
      return;
    }

    // Prevent duplicate transfers
    if (hasTransferredRef.current || exchangingTokens.has(transferToken)) {
      return;
    }

    hasTransferredRef.current = true;
    exchangingTokens.add(transferToken);

    // Complete transfer flow: exchange token, set session, load tenant, navigate
    const completeTransfer = async () => {
      try {
        // Step 1: Exchange transfer token for access/refresh tokens
        const tokenResponse =
          await authService.exchangeTransferToken(transferToken);

        // Step 2: Set the session in the auth client
        const { error: sessionError } = await authClient.setSession({
          access_token: tokenResponse.access_token,
          refresh_token: tokenResponse.refresh_token,
          expires_in: tokenResponse.expires_in || 900, // 15 minutes default
        });

        if (sessionError) {
          throw new Error(sessionError.message || "Failed to set session");
        }

        // Step 3: Load user and tenant data
        const userDetails = await userService.getMyUser();

        // Breeze UI is restricted to central users only
        if (!userDetails.is_central_user) {
          toast.error(
            "Access Denied: this is restricted to central users only. Please contact your administrator if you believe this is an error.",
          );
          if (window.parent !== window.self) {
            window.parent.postMessage({ type: "exit-management" }, "*");
          } else {
            window.location.href = `${config.centralAuthUrl}/auth/logout?error=access_denied`;
          }
          return;
        }

        // Step 4: Load and verify tenant access
        const selectedTenant = await tenantService.getTenantById(
          Number(targetOrgId),
        );

        if (!selectedTenant) {
          toast.error("You don't have access to this Organisation.");
          if (window.parent !== window.self) {
            window.parent.postMessage({ type: "exit-management" }, "*");
          } else {
            window.location.href = `${config.centralAuthUrl}/auth/logout?error=no_access_to_org`;
          }
          return;
        }

        // Step 5: Store tenant ID
        localStorage.setItem(TENANT_ID_KEY, selectedTenant.id.toString());

        // Step 6: Clean up URL parameters
        const cleanUrlParams = new URLSearchParams(window.location.search);
        cleanUrlParams.delete("transfer_token");
        cleanUrlParams.delete("target_org_id");
        const newUrl = cleanUrlParams.toString()
          ? `${window.location.pathname}?${cleanUrlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);

        // Step 7: Navigate to target or dashboard
        const orgPathPrefix = `/org/${targetOrgId}`;
        const redirectTo = urlParams.get("redirect_to");

        let targetPath = `${orgPathPrefix}/dashboard`;

        if (redirectTo) {
          // If the redirect path already starts with the org prefix, use it as is
          if (redirectTo.startsWith(orgPathPrefix)) {
            targetPath = redirectTo;
          } else {
            // Otherwise, append it (ensuring no double slashes)
            const cleanRedirect = redirectTo.startsWith("/")
              ? redirectTo
              : `/${redirectTo}`;
            targetPath = `${orgPathPrefix}${cleanRedirect}`;
          }
        }

        navigate(targetPath, { replace: true });
      } catch (err: any) {
        exchangingTokens.delete(transferToken);
        const errorMessage =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to complete transfer";
        setError(errorMessage);
        toast.error(errorMessage);
        setInvalid(true);
        hasTransferredRef.current = false; // Allow retry on error
      }
    };

    completeTransfer();
  }, [navigate]);

  if (invalid || error) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 16,
            paddingTop: 56,
          }}
        >
          <span
            style={{
              color: "var(--red-11)",
              fontSize: 24,
              marginBottom: 16,
              fontWeight: "bold",
              fontFamily: "inherit",
            }}
          >
            {error || "Invalid instance transfer request"}
          </span>
          <Button color="red" variant="soft" size="3" asChild>
            <a href={config.centralAuthUrl} target="_parent">Go back to MyAnythink</a>
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default Transfer;
